import React from 'react'
import { TableColumnDefinition } from '@fluentui/react-components'
import { AddRiskAnalysisDTO, ConsequenceAndProbabilityRisk, RiskAnalysis, RiskAnalysisDisplayDTO, UserAccount } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { api } from './apiHelper'
import { handleMessage } from './stateHelper'
import { UseFormReset } from 'react-hook-form'
import { IListState } from '../interfaces/IListState'
import { getRiskAnalysisTypeString } from './enumHelper'
import { Avatar } from '../components/common/avatar/Avatar'
import { t } from '../i18n/i18n'
import { getFullName } from './accountHelper'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { compareByDate, formatDate } from './stringHelper'
import { isSystemAdmin } from './permissionHelper'

export enum RiskManagementTabs {
   Ongoing = 'ongoing',
   Concluded = 'concluded',
}

export const getRiskManagementColumns = (
   setRiskManagementState: React.Dispatch<React.SetStateAction<IListState<RiskAnalysisDisplayDTO>>>,
   userAccount: UserAccount
): TableColumnDefinition<RiskAnalysis>[] => [
   {
      columnId: 'name',
      renderHeaderCell: () => t('NameOfAssesment'),
      renderCell: (item) => item.name,
      compare: (a, b) => a.name.localeCompare(b.name),
   },
   {
      columnId: 'type',
      renderHeaderCell: () => t('Type'),
      renderCell: (item) => getRiskAnalysisTypeString(item.type),
      compare: (a, b) => getRiskAnalysisTypeString(a.type).localeCompare(getRiskAnalysisTypeString(b.type)),
   },
   {
      columnId: 'modifiedBy',
      renderHeaderCell: () => t('ModifiedBy'),
      renderCell: (item) => <Avatar name={getFullName(item.modifiedBy)} />,
      compare: (a, b) => getFullName(a.modifiedBy).localeCompare(getFullName(b.modifiedBy)),
   },
   {
      columnId: 'modifed',
      renderHeaderCell: () => t('Modified'),
      renderCell: (item) => {
         return formatDate(item.modified, true)
      },
      compare: (a, b) => compareByDate(b.modified, a.modified),
   },
   {
      columnId: 'menu',
      renderHeaderCell: () => <div style={{ width: '35px' }}>&nbsp;</div>,
      renderCell: (item) => (
         <MoreOptionsMenuButton
            disabled={!isRiskAnalysisOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
            disabledToolTipMessage={t('DeleteRiskAnalysisNoAccessMessage')}
            item={item}
            deleteFunction={deleteRiskAnalysis}
            setState={setRiskManagementState}
            deleteMessage={t('DeleteRiskAssessment')}
            deleteConfirmationMessage={t('DeleteRiskAssessmentConfirmationMessage')}
         />
      ),
      compare: () => 0,
   },
]

export const addRiskAnalysis = async (
   setRiskManagementState: React.Dispatch<React.SetStateAction<IListState<RiskAnalysis>>>,
   setError: React.Dispatch<React.SetStateAction<string>>,
   reset: UseFormReset<RiskAnalysis>,
   dataContext: IDataContext,
   data: RiskAnalysis
) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.organisationId = organisation.id
      data.modifiedById = userAccount.id
      data.modifiedBy = userAccount
      data.createdBy = userAccount
      data.createdById = userAccount.id

      const addRiskanalysisDto: AddRiskAnalysisDTO = {
         riskAnalysis: data,
         organisationId: organisation.id,
      }

      const response = await api().addRiskAnalysis(addRiskanalysisDto)
      const addedAnalysis = response.data
      reset(addedAnalysis, { keepIsSubmitSuccessful: true, keepIsSubmitted: true })
      setRiskManagementState((prev) => ({ ...prev, allItems: [...prev.allItems, addedAnalysis], open: false }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullySaved', { Item: t('RiskAssesment') }))
   } catch (error: any) {
      setError(error.error.Message)
      reset()
      setRiskManagementState((prev) => ({ ...prev, open: false }))
      handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: t('RiskAssesment') }))
   }
}

export const deleteRiskAnalysis = async (
   id: string,
   dataContext: IDataContext,
   setRiskManagementState: React.Dispatch<React.SetStateAction<IListState<RiskAnalysis>>>
) => {
   try {
      const { organisation } = dataContext.state

      await api().deleteRiskAnalysis(id)

      const riskAnalysesResponse = await api().getRiskAnalysesByOrganisationId(organisation.id)
      const riskAnalyses = riskAnalysesResponse.data

      setRiskManagementState((prev) => ({ ...prev, allItems: riskAnalyses, open: false }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('RiskAssesment') }))
   } catch (error: any) {
      setRiskManagementState((prev) => ({ ...prev, open: false }))
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('RiskAssesment') }))
   }
}

export const calculateRiskLevelValue = (risk: ConsequenceAndProbabilityRisk) => {
   const highestConsequenceValue = Math.max(risk.consequenceLifeHealth, risk.consequenceEconomy, risk.consequenceBrand, risk.consequenceEnviroment)
   return risk.probability * highestConsequenceValue
}

export const sortConsequenceRisks = (risks: ConsequenceAndProbabilityRisk[]) => {
   return [...risks].sort((a, b) => {
      const riskLevelDiff = (a.riskLevel ?? Number.POSITIVE_INFINITY) - (b.riskLevel ?? Number.POSITIVE_INFINITY)
      if (riskLevelDiff !== 0) return riskLevelDiff

      const valueDiff = calculateRiskLevelValue(b) - calculateRiskLevelValue(a)
      if (valueDiff !== 0) return valueDiff

      const healthDiff = (b.consequenceLifeHealth ?? -Infinity) - (a.consequenceLifeHealth ?? -Infinity)
      if (healthDiff !== 0) return healthDiff

      const economyDiff = (b.consequenceEconomy ?? -Infinity) - (a.consequenceEconomy ?? -Infinity)
      if (economyDiff !== 0) return economyDiff

      const probabilityDiff = (b.probability ?? -Infinity) - (a.probability ?? -Infinity)
      if (probabilityDiff !== 0) return probabilityDiff

      const aMax = Math.max(a.consequenceEnviroment ?? -Infinity, a.consequenceBrand ?? -Infinity)
      const baMax = Math.max(b.consequenceEnviroment ?? -Infinity, b.consequenceBrand ?? -Infinity)
      return baMax - aMax
   })
}

export const isRiskAnalysisOwnerOrCreator = (riskAnalysis: RiskAnalysis, userAccount: UserAccount) => {
   if (riskAnalysis.createdById === userAccount.id) return true
   if (riskAnalysis.analysisOwnerId === userAccount.id) return true
   return false
}

export const isRiskAnalysisOwnerLeaderOrCreator = (riskAnalysis: RiskAnalysis, userAccount: UserAccount) => {
   if (riskAnalysis.createdById === userAccount.id) return true
   if (riskAnalysis.analysisOwnerId === userAccount.id) return true
   if (riskAnalysis.analysisLeaderId === userAccount.id) return true
   return false
}
